/**
 * Upcoming Highlights: a thin homepage banner showing the single soonest
 * item from backbone-craftcore-erpnext's live, guest-readable
 * craftcore.market_calendar.get_homepage_highlights RPC -- either an
 * upcoming market event or a special-hours day (e.g. a holiday closure) at
 * a retail location. Requests a longer lookahead than the backend's own
 * 7-day default (DEFAULT_LOOKAHEAD_DAYS below, merchant-editable via the
 * section's "Days to look ahead" setting) -- a banner meant to catch a
 * customer's attention on the homepage should surface things more than a
 * week out; the backend's tighter default stays as-is for any other
 * consumer of this RPC. Sorts by (date, type, title), backend-side.
 *
 * Renders nothing at all when the list is empty or the request fails: this
 * is meant to be unobtrusive, and a quiet week should leave no trace on the
 * page. The wrapper starts `hidden` in the Liquid and is only unhidden here
 * once there's an item to show.
 *
 * Cross-origin fetch, same as retail-locator-map.js: the RPC is public,
 * read-only data, so no App Proxy or signature is involved.
 *
 * "More Details" link, always computed from the item itself (no merchant
 * setting involved):
 * - market_event links to its own page, /pages/vendor-events?event=<name>
 *   (the same deep-link pattern vendor-calendar.js uses, backed by
 *   sections/vendor-event-detail.liquid).
 * - special_hours links to /pages/where-we-sell?location=<name> -- the
 *   "Where to Buy Locally" list, deep-linked to scroll to and briefly
 *   highlight that specific location's card (retail-locator-map.js). From
 *   there a customer can see the location's own address/description and,
 *   once those fields exist, follow through to its website or socials.
 */

import { formatTime, parseLocalDate } from '@theme/vendor-event-format';

const HIGHLIGHTS_API_URL = 'https://erp.backbonecraftco.com/api/method/craftcore.market_calendar.get_homepage_highlights';
const EVENT_DETAIL_PAGE_URL = '/pages/vendor-events';
const LOCATION_LIST_PAGE_URL = '/pages/where-we-sell';
const DEFAULT_LOOKAHEAD_DAYS = 14;
const MIN_LOOKAHEAD_DAYS = 1;
const MAX_LOOKAHEAD_DAYS = 31; // mirrors backend's HIGHLIGHT_MAX_DAYS

const TYPE_ICONS = {
  market_event: '\u{1F4C5}',
  special_hours: '\u{1F552}',
};

/** Shown only when the backend sends no `title` for an item. The backend's
 * `name` field is an internal ERP id (e.g. "4rajf0f7hc"), so it is never
 * displayed. */
const TYPE_FALLBACK_TITLES = {
  market_event: 'Market event',
  special_hours: 'Special hours',
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RANGE_PATTERN = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/;

/** An item is usable when it has a well-formed date. Anything else is
 * skipped rather than rendered half-empty. */
function isUsableHighlight(item) {
  return typeof item?.date === 'string' && ISO_DATE_PATTERN.test(item.date);
}

/** "Sat, Nov 7" from a "YYYY-MM-DD" date, read as a local date so it never
 * shifts a day in timezones west of UTC. */
function formatHighlightDate(dateString) {
  return parseLocalDate(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** "10:00 AM – 4:00 PM" from the backend's 24-hour "10:00 - 16:00" string.
 * The literal "Closed" (a closed special-hours day) and anything unexpected
 * pass through unchanged. */
function formatHighlightTime(time) {
  if (typeof time !== 'string' || time.trim() === '') return null;
  const match = TIME_RANGE_PATTERN.exec(time.trim());
  if (!match) return time.trim();
  return `${formatTime(match[1])} – ${formatTime(match[2])}`;
}

function getHighlightTitle(item) {
  const title = typeof item.title === 'string' ? item.title.trim() : '';
  return title || TYPE_FALLBACK_TITLES[item.type] || 'Upcoming highlight';
}

/** The date / time / detail parts, in display order, with empty parts
 * dropped. e.g. ["Sat, Nov 7", "10:00 AM – 4:00 PM", "Cedar Falls, IA"]. */
function getHighlightMetaParts(item) {
  const detail = typeof item.detail === 'string' ? item.detail.trim() : '';
  return [formatHighlightDate(item.date), formatHighlightTime(item.time), detail || null].filter(Boolean);
}

/** The merchant-configured lookahead window (section setting
 * `data-upcoming-highlights-days`), clamped to the backend's own allowed
 * range. Falls back to DEFAULT_LOOKAHEAD_DAYS for a blank, non-numeric, or
 * out-of-range value -- never sends a `days` the backend would reject. */
function getLookaheadDays(root) {
  const raw = Number.parseInt(root.dataset.upcomingHighlightsDays, 10);
  if (!Number.isFinite(raw) || raw < MIN_LOOKAHEAD_DAYS || raw > MAX_LOOKAHEAD_DAYS) {
    return DEFAULT_LOOKAHEAD_DAYS;
  }
  return raw;
}

/** Where "More Details" should point, or null to hide the link (no usable
 * `name` to link with). See the module docstring for the per-type URLs. */
function getHighlightLink(item) {
  if (!item.name) return null;
  if (item.type === 'market_event') {
    return `${EVENT_DETAIL_PAGE_URL}?event=${encodeURIComponent(item.name)}`;
  }
  if (item.type === 'special_hours') {
    return `${LOCATION_LIST_PAGE_URL}?location=${encodeURIComponent(item.name)}`;
  }
  return null;
}

class UpcomingHighlights {
  constructor(root) {
    this.root = root;
    this.iconEl = root.querySelector('[data-upcoming-highlights-icon]');
    this.titleEl = root.querySelector('[data-upcoming-highlights-title]');
    this.metaEl = root.querySelector('[data-upcoming-highlights-meta]');
    this.linkEl = root.querySelector('[data-upcoming-highlights-link]');

    this.load();
  }

  async load() {
    try {
      const days = getLookaheadDays(this.root);
      const response = await fetch(`${HIGHLIGHTS_API_URL}?days=${days}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const data = await response.json();
      const highlights = Array.isArray(data?.message) ? data.message : [];

      // The backend returns items already sorted soonest-first, so the first
      // usable one is the one to show. An empty list leaves the banner hidden.
      const soonest = highlights.find(isUsableHighlight);
      if (soonest) this.show(soonest);
    } catch (error) {
      console.warn('Failed to load upcoming highlights:', error);
    }
  }

  show(item) {
    if (this.iconEl) this.iconEl.textContent = TYPE_ICONS[item.type] || '';
    if (this.titleEl) this.titleEl.textContent = getHighlightTitle(item);
    if (this.metaEl) this.metaEl.textContent = getHighlightMetaParts(item).join(' · ');

    if (this.linkEl) {
      const href = getHighlightLink(item);
      if (href) {
        this.linkEl.href = href;
        this.linkEl.hidden = false;
      } else {
        this.linkEl.hidden = true;
      }
    }

    this.root.hidden = false;
  }
}

document.querySelectorAll('[data-upcoming-highlights]').forEach((root) => new UpcomingHighlights(root));
